import React, { useRef, useState, useCallback, useEffect } from "react"
import _get from "lodash/get"
import debounce from "lodash/debounce"

import Input from "@/components/core/Input/Input"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import Button from "@/components/core/Button/Button"
import Dropdown from "@/components/core/SelectDropdown/SelectDropdown"

import {
  destructureAddress,
  validateField,
  getStateCode,
  validatePhone,
} from "@/utils/helper"
import { getAddressSuggestions } from "@/components/checkout/checkoutHelperFunction"
import {
  getStateName,
  getCountryName,
  convertToPhoneNumber,
} from "@/components/RegisterOnlineProduct/v1/RegisterOnlineHelper"
import {
  getAddAnotherAddressAnalytics,
  addAddAddressAnalytics,
} from "@/components/AccountSettings/v1/AddressForm/analytics"

import PlusIcon from "@/public/icons/plus-small.svg"
import phoneIcon from "@/public/icons/phone-icon.svg"

export const AddressForm = ({
  i18n = {},
  country = "",
  countries = [],
  onAdd = () => {},
  onEdit = () => {},
  stateData = [],
  data: propData = {},
  onCloseRef = {},
  isDefault = false,
  ...props
}) => {
  const editInitialValue = {
    firstName: {
      value: _get(propData, "firstName.value", ""),
      error: false,
      message: "",
    },
    lastName: {
      value: _get(propData, "lastName.value", ""),
      error: false,
      message: "",
    },
    address1: {
      value: _get(propData, "address1.value", ""),
      error: false,
      message: "",
    },
    address2: {
      value: _get(propData, "address2.value", ""),
      error: false,
      message: "",
    },
    city: {
      value: _get(propData, "city.value", ""),
      error: false,
      message: "",
    },
    state: {
      value: _get(propData, "state.value", ""),
      error: false,
      message: "",
    },
    zipcode: {
      value: _get(propData, "zipcode.value", ""),
      error: false,
      message: "",
    },
    label: {
      show: isDefault,
      value: _get(propData, "label.value", ""),
    },
    addresstypes: {
      value: _get(propData, "addresstypes.value", ""),
      error: false,
      message: "",
    },
    phone: {
      value: _get(propData, "phone.value", ""),
      error: false,
      message: "",
    },
  }

  const addInitialValue = [
    {
      firstName: {
        value: _get(propData, "firstName.value", ""),
        error: false,
        message: "",
      },
      lastName: {
        value: _get(propData, "lastName.value", ""),
        error: false,
        message: "",
      },
      address1: {
        value: _get(propData, "address1.value", ""),
        error: false,
        message: "",
      },
      address2: {
        value: _get(propData, "address2.value", ""),
        error: false,
        message: "",
      },
      city: {
        value: _get(propData, "city.value", ""),
        error: false,
        message: "",
      },
      state: {
        value: getStateName(
          _get(propData, "state.value", ""),
          _get(propData, "country.value", country ?? ""),
          countries
        ),
        error: false,
        message: "",
      },
      zipcode: {
        value: _get(propData, "zipcode.value", ""),
        error: false,
        message: "",
      },
      country: {
        value: getCountryName(
          _get(propData, "country.value", country ?? ""),
          countries
        ),
        error: false,
        message: "",
      },
      label: { show: false, value: _get(propData, "label.value", "") },
      addresstypes: {
        value: _get(propData, "addresstypes.value", ""),
        error: false,
        message: "",
      },
      phone: {
        value: _get(propData, "phone.value", ""),
        error: false,
        message: "",
      },
    },
  ]
  const { edit = false } = propData
  const [data, setData] = useState(edit ? editInitialValue : addInitialValue)
  const [isAddressAdded, setIsAddressAdded] = useState(false)
  const inputRefs = useRef({})

  const [suggestions, setSuggestions] = useState(
    edit
      ? []
      : [
          {
            value: [],
          },
        ]
  )

  const handleAddAddress = () => {
    const value = [...data]
    const list = [...suggestions]
    value.push({
      firstName: {
        value: _get(propData, "firstName.value", ""),
        error: false,
        message: "",
      },
      lastName: {
        value: _get(propData, "lastName.value", ""),
        error: false,
        message: "",
      },
      address1: {
        value: _get(propData, "address1.value", ""),
        error: false,
        message: "",
      },
      address2: {
        value: _get(propData, "address2.value", ""),
        error: false,
        message: "",
      },
      city: {
        value: _get(propData, "city.value", ""),
        error: false,
        message: "",
      },
      state: {
        value: getStateName(
          _get(propData, "state.value", ""),
          _get(propData, "country.value", country),
          countries
        ),
        error: false,
        message: "",
      },
      zipcode: {
        value: _get(propData, "zipcode.value", ""),
        error: false,
        message: "",
      },
      country: {
        value: getCountryName(
          _get(propData, "country.value", country ?? ""),
          countries
        ),
        error: false,
        message: "",
      },
      label: { show: false, value: _get(propData, "label.value", "") },
      addresstypes: {
        value: _get(propData, "addresstypes.value", ""),
        error: false,
        message: "",
      },
      phone: {
        value: _get(propData, "phone.value", ""),
        error: false,
        message: "",
      },
    })
    list.push({
      value: [],
    })
    setIsAddressAdded(true)
    setData(value)
    setSuggestions(list)
  }

  const handleSubmit = async () => {
    if (edit) {
      const obj = {}
      let valid = true
      const errorData = []
      const errorMsg = []
      if (!validatePhone(data?.phone?.value ? data?.phone?.value : "")) {
        obj.phone = { ...data?.phone, error: true, message: i18n?.validPhone }
        valid = false
        errorMsg.push(i18n?.validPhone)
        errorData.push("phone")
      }
      if (!data?.address1?.value) {
        obj.address1 = {
          ...data?.address1,
          error: true,
          message: i18n?.addressError,
        }
        valid = false
        errorData.push("address1")
        errorMsg.push(i18n?.addressError)
      }
      if (!data?.firstName?.value) {
        obj.firstName = {
          ...data?.firstName,
          error: true,
          message: i18n?.fnameError,
        }
        valid = false
        errorData.push("firstName")
        errorMsg.push(i18n?.fnameError)
      }
      if (!data?.lastName?.value) {
        obj.lastName = {
          ...data?.lastName,
          error: true,
          message: i18n?.lnameError,
        }
        valid = false
        errorData.push("lastName")
        errorMsg.push(i18n?.lnameError)
      }
      if (!data?.city?.value) {
        obj.city = { ...data?.city, error: true, message: i18n?.cityError }
        valid = false
        errorData.push("city")
        errorMsg.push(i18n?.cityError)
      }
      if (!data?.state?.value) {
        obj.state = { ...data?.state, error: true, message: i18n?.stateError }
        valid = false
        errorData.push("state")
        errorMsg.push(i18n?.stateError)
      }
      if (!data?.zipcode?.value) {
        obj.zipcode = {
          ...data?.zipcode,
          error: true,
          message: i18n?.zipCodeError,
        }
        valid = false
        errorData.push("zipcode")
        errorMsg.push(i18n?.zipCodeError)
      } else if (data?.zipcode?.value?.length < 5) {
        obj.zipcode = {
          ...data?.zipcode,
          error: true,
          message: i18n?.zipCodeMinError,
        }
        valid = false
        errorData.push("zipcode")
        errorMsg.push(i18n?.zipCodeError)
      } else {
        validateField("zipCodeNumeric", data?.zipcode?.value)?.then(res => {
          if (!res) {
            obj.zipcode = {
              ...data?.zipcode,
              error: true,
              message: i18n?.zipCodeError,
            }
            valid = false
            errorData.push("zipcode")
            errorMsg.push(i18n?.zipCodeError)
          }
        })
      }

      const newData = { ...data, ...obj }
      setData(newData)
      errorData.forEach((item, i) => {
        if (i === 0) document.getElementById(`edit-address-${item}`)?.focus()
      })
      if (valid) {
        if (edit) {
          onEdit(data)
          addAddAddressAnalytics("success", "", edit)
        } else {
          onAdd(data)
        }
      } else {
        addAddAddressAnalytics("failure", errorMsg, edit)
      }
    } else {
      let valid = true
      const errorData = []
      const errorMsg = []
      Promise.all(
        data.map(async (innerData, index) => {
          const obj = {}
          if (!validatePhone(innerData?.phone?.value)) {
            obj["phone"] = {
              ...innerData?.phone,
              error: true,
              message: i18n?.validPhone,
            }
            valid = false
            errorMsg.push(i18n?.validPhone)
            errorData.push(`phone${index}`)
          } else {
            obj["phone"] = { ...innerData?.phone, error: false, message: "" }
          }
          if (!innerData?.address1?.value) {
            obj["address1"] = {
              ...innerData?.address1,
              error: true,
              message: i18n?.addressError,
            }
            valid = false
            errorMsg.push(i18n?.addressError)

            errorData.push(`address1${index}`)
          } else {
            obj["address1"] = {
              ...innerData?.address1,
              error: false,
              message: "",
            }
          }
          if (!innerData?.firstName?.value) {
            obj["firstName"] = {
              ...innerData?.firstName,
              error: true,
              message: i18n?.fnameError,
            }
            valid = false
            errorMsg.push(i18n?.fnameError)

            errorData.push(`firstName${index}`)
          } else {
            obj["firstName"] = {
              ...innerData?.firstName,
              error: false,
              message: "",
            }
          }
          if (!innerData?.lastName?.value) {
            obj["lastName"] = {
              ...innerData?.lastName,
              error: true,
              message: i18n?.lnameError,
            }
            valid = false
            errorMsg.push(i18n?.lnameError)

            errorData.push(`lastName${index}`)
          } else {
            obj["lastName"] = {
              ...innerData?.lastName,
              error: false,
              message: "",
            }
          }
          if (!innerData?.city?.value) {
            obj["city"] = {
              ...innerData?.city,
              error: true,
              message: i18n?.cityError,
            }
            valid = false
            errorMsg.push(i18n?.cityError)

            errorData.push(`city${index}`)
          } else {
            obj["city"] = { ...innerData?.city, error: false, message: "" }
          }
          if (!innerData?.state?.value) {
            obj["state"] = {
              ...innerData?.state,
              error: true,
              message: i18n?.stateError,
            }
            valid = false
            errorMsg.push(i18n?.stateError)

            errorData.push(`state${index}`)
          } else {
            obj["state"] = { ...innerData?.state, error: false, message: "" }
          }
          if (!innerData?.zipcode?.value) {
            obj["zipcode"] = {
              ...innerData?.zipcode,
              error: true,
              message: i18n?.zipCodeError,
            }
            valid = false
            errorMsg.push(i18n?.zipCodeError)
            errorData.push(`zipcode${index}`)
          } else if (innerData?.zipcode?.value?.length < 5) {
            obj["zipcode"] = {
              ...innerData?.zipcode,
              error: true,
              message: i18n?.zipCodeMinError,
            }
            valid = false
            errorMsg.push(i18n?.zipCodeError)
            errorData.push(`zipcode${index}`)
          } else {
            validateField("zipCodeNumeric", innerData?.zipcode?.value)?.then(
              res => {
                if (res) {
                  obj["zipcode"] = {
                    ...innerData?.zipcode,
                    error: false,
                    message: "",
                  }
                } else {
                  obj["zipcode"] = {
                    ...innerData?.zipcode,
                    error: true,
                    message: i18n?.zipCodeError,
                  }
                  valid = false
                  errorMsg.push(i18n?.zipCodeError)
                  errorData.push(`zipcode${index}`)
                }
              }
            )
          }

          const tempData = { ...innerData, ...obj }

          return tempData
        })
      ).then(newData => {
        setData(newData)
        errorData.forEach((item, i) => {
          if (i === 0) document.getElementById(`add-address-${item}`)?.focus()
        })
        if (valid) {
          addAddAddressAnalytics("success")
          onAdd(data)
        } else {
          addAddAddressAnalytics("failure", errorMsg)
        }
      })
    }
  }

  const handleAddChange = async (index, name, e) => {
    const value = e?.target?.value
    if (edit) {
      if (name === "label") {
        setData({
          ...data,
          [name]: { ...data?.label, show: e?.target?.checked },
        })
      } else if (name === "state") {
        setData({
          ...data,
          [name]: { value: e, error: false, message: "" },
        })
      } else if (name === "zipcode") {
        if ((await validateField("zipCodeNumeric", value)) || value === "") {
          setData({
            ...data,
            [name]: {
              value: value,
              error: false,
              message: "",
            },
          })
        }
      } else {
        setData({
          ...data,
          [name]: {
            value: name === "phone" ? convertToPhoneNumber(value) : value,
            error: false,
            message: "",
          },
        })
      }
    } else {
      const addArry = [...data]
      if (name === "state") {
        addArry[index][name].value = e
      } else if (name === "label") {
        addArry.forEach((obj, i) => {
          if (index === i) {
            Object.assign(obj?.label, { show: e?.target?.checked })
          } else {
            Object.assign(obj?.label, { show: !e?.target?.checked })
          }
        })
      } else if (name === "phone") {
        addArry[index][name].value = convertToPhoneNumber(value)
      } else if (name === "zipcode") {
        if ((await validateField("zipCodeNumeric", value)) || value === "") {
          addArry[index][name].value = value
        }
      } else {
        addArry[index][name].value = value
      }
      setData(addArry)
    }
  }
  const handleRemoveAddress = index => {
    const addressArry = [...data]
    addressArry.splice(index, 1)
    setData(addressArry)
  }

  const removeAddressEventInfo = JSON.stringify({
    clickInternalLinks: "true",
    eventAction: "my account:account settings:modal:add address:remove",
    eventName: "my account:account settings:modal:add address:remove",
    eventStatus: "n/a",
    eventMsg: "n/a",
    eventType: "navigation",
    internalLinkName: "remove",
    internalLinkPosition: "my account",
    internalLinkType: "my account:addresses",
    internalLinkURL: "n/a",
    internalLinkZoneName: "my account:account settings:modal:add address",
  })

  const handleAddressChange = (index, e) => {
    handleAddChange(index, "address1", e)
    if (edit) {
      getSuggestionsEdit(e?.target?.value)
    } else {
      getSuggestions(e?.target?.value, index)
    }
  }

  const getSuggestions = useCallback(
    debounce((addr, index) => {
      getAddressSuggestions(addr)?.then(suggData => {
        const list = [...suggestions]
        list.forEach((obj, i) => {
          if (index === i) {
            Object.assign(obj, { value: suggData })
          }
        })
        setSuggestions(list)
      })
    }, 300),
    [suggestions]
  )
  const getSuggestionsEdit = useCallback(
    debounce(addr => {
      getAddressSuggestions(addr)?.then(suggData => setSuggestions(suggData))
    }, 300),
    []
  )
  const onSuggestionSelect = (index, val) => {
    if (edit) {
      setSuggestions([])
      const addr = destructureAddress(val)

      setData({
        ...data,
        address1: { value: addr?.address, error: false, message: "" },
        city: { value: addr?.city, error: false, message: "" },
        state: {
          value: getStateCode(addr?.state, country, countries) || addr?.state,
          error: false,
          message: "",
        },
        zipcode: { value: addr?.zipcode, error: false, message: "" },
      })
    } else {
      const list = [...suggestions]
      list[index] = { ...suggestions[index], value: [] }
      setSuggestions(list)
      const addr = destructureAddress(val)
      const dataItems = [...data]
      const addressItems = {
        ...data[index],
        address1: { value: addr.address, error: false, message: "" },
        city: { value: addr.city, error: false, message: "" },
        state: {
          value: getStateCode(addr.state, country, countries) || addr?.state,
          error: false,
          message: "",
        },
        zipcode: { value: addr.zipcode, error: false, message: "" },
      }
      dataItems[index] = addressItems
      setData(dataItems)
    }
  }
  const clearDataOnClose = () => {
    setData([
      {
        firstName: {
          value: _get(propData, "firstName.value", ""),
          error: false,
          message: "",
        },
        lastName: {
          value: _get(propData, "lastName.value", ""),
          error: false,
          message: "",
        },
        address1: {
          value: _get(propData, "address1.value", ""),
          error: false,
          message: "",
        },
        address2: {
          value: _get(propData, "address2.value", ""),
          error: false,
          message: "",
        },
        city: {
          value: _get(propData, "city.value", ""),
          error: false,
          message: "",
        },
        state: {
          value: getStateName(
            _get(propData, "state.value", ""),
            _get(propData, "country.value", country ?? ""),
            countries
          ),
          error: false,
          message: "",
        },
        zipcode: {
          value: _get(propData, "zipcode.value", ""),
          error: false,
          message: "",
        },
        country: {
          value: getCountryName(
            _get(propData, "country.value", country ?? ""),
            countries
          ),
          error: false,
          message: "",
        },
        label: { show: false, value: _get(propData, "label.value", "") },
        addresstypes: {
          value: _get(propData, "addresstypes.value", ""),
          error: false,
          message: "",
        },
        phone: {
          value: _get(propData, "phone.value", ""),
          error: false,
          message: "",
        },
      },
    ])
  }
  onCloseRef.clearDataOnClose = clearDataOnClose

  const onBlurInput = async (index, name, value) => {
    if (!edit) {
      const dataCopy = [...data]
      switch (name) {
        case "phone":
          if (!validatePhone(value)) {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: true,
              message: i18n?.validPhone,
            }
            setData(dataCopy)
          } else {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: false,
              message: "",
            }
            setData(dataCopy)
          }
          break
        case "address1":
          if (!value) {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: true,
              message: i18n?.addressError,
            }
            setData(dataCopy)
          } else {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: false,
              message: "",
            }
            setData(dataCopy)
          }
          break
        case "city":
          if (!value) {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: true,
              message: i18n?.cityError,
            }
            setData(dataCopy)
          } else {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: false,
              message: "",
            }
            setData(dataCopy)
          }
          break
        case "state":
          if (!value) {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: true,
              message: i18n?.stateError,
            }
            setData(dataCopy)
          } else {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: false,
              message: "",
            }
            setData(dataCopy)
          }
          break
        case "zipcode":
          if (!value) {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: true,
              message: i18n?.zipCodeError,
            }
            setData(dataCopy)
          } else if (value.length < 5) {
            dataCopy[index][name] = {
              ...dataCopy[index][name],
              error: true,
              message: i18n?.zipCodeMinError,
            }
            setData(dataCopy)
          } else {
            await validateField("zipCodeNumeric", value)?.then(res => {
              if (res) {
                dataCopy[index][name] = {
                  ...dataCopy[index][name],
                  error: false,
                  message: "",
                }
                setData(dataCopy)
              } else {
                dataCopy[index][name] = {
                  ...dataCopy[index][name],
                  error: true,
                  message: i18n?.zipCodeError,
                }
                setData(dataCopy)
              }
            })
          }

          break
        default:
          return
      }
    }
  }

  const finalForm = Array.isArray(data) ? data : [data]
  useEffect(() => {
    const input = inputRefs.current[`add-address-firstName${data.length - 1}`]
    if (input && isAddressAdded) {
      input.focus()
    }
    isAddressAdded ? setIsAddressAdded(false) : null
  }, [data])

  const { save = "" } = i18n
  return (
    <div className="address-form container px-0 mx-0">
      <span className="address-form__required-fields">
        {`${i18n?.requiredFields}`}
      </span>
      {finalForm
        ? finalForm?.map((addData, index) => (
            <div key={index}>
              {index > 0 ? (
                <div className="address-form__addressTitle">
                  <span>
                    {i18n?.address} {index + 1}
                  </span>
                  <span
                    data-gbh-data-layer={removeAddressEventInfo}
                    onClick={() => handleRemoveAddress(index)}
                    className="address-form__removeaddress gbh-data-layer"
                    role="button"
                    tabindex="0"
                  >
                    {i18n?.remove}
                  </span>
                </div>
              ) : null}
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-firstName`
                        : `add-address-firstName${index}`
                    }
                    ref={el =>
                      (inputRefs.current[`add-address-firstName${index}`] = el)
                    }
                    type="char"
                    value={addData?.firstName?.value}
                    label={`${i18n.firstName}*`}
                    placeholder={i18n?.firstName}
                    showError={addData?.firstName?.error}
                    errorMessage={addData?.firstName?.message}
                    onChange={e => handleAddChange(index, "firstName", e)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-lastName`
                        : `add-address-lastName${index}`
                    }
                    type="text"
                    value={addData?.lastName?.value}
                    label={`${i18n.lastName}*`}
                    placeholder={i18n?.lastName}
                    showError={addData?.lastName?.error}
                    errorMessage={addData?.lastName?.message}
                    onChange={e => handleAddChange(index, "lastName", e)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-address1`
                        : `add-address-address1${index}`
                    }
                    type="text"
                    value={addData?.address1?.value}
                    label={`${i18n?.address}*`}
                    placeholder={`${i18n?.address}*`}
                    onChange={e => handleAddressChange(index, e)}
                    onBlur={e =>
                      onBlurInput(index, "address1", e?.target?.value)
                    }
                    showError={addData?.address1?.error}
                    errorMessage={addData?.address1?.message}
                    suggestions={edit ? suggestions : suggestions[index]?.value}
                    onSelect={val => onSuggestionSelect(index, val)}
                    autoComplete={true}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-floor`
                        : `add-address-address2${index}`
                    }
                    type="text"
                    value={addData?.address2?.value}
                    placeholder={i18n?.aptFloorSuite}
                    label={i18n?.aptFloorSuite}
                    showError={addData?.address2?.error}
                    errorMessage={addData?.address2?.message}
                    onChange={e => handleAddChange(index, "address2", e)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={edit ? `edit-address-city` : `add-address-city${index}`}
                    type="text"
                    value={addData?.city?.value}
                    label={`${i18n?.city}*`}
                    showError={addData?.city?.error}
                    errorMessage={addData?.city?.message}
                    onChange={e => handleAddChange(index, "city", e)}
                    onBlur={e => onBlurInput(index, "city", e?.target?.value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="left-half-field col-6">
                  <Dropdown
                    id={
                      edit ? `edit-address-state` : `add-address-state${index}`
                    }
                    data={stateData ?? []}
                    label={`${i18n?.state}*`}
                    value={addData?.state?.value}
                    showError={addData?.state?.error}
                    errorMessage={addData?.state?.message}
                    onChange={val => handleAddChange(index, "state", val)}
                    onClose={() =>
                      onBlurInput(index, "state", addData?.state?.value)
                    }
                    buttonId="add-address-state"
                    ariaStateText={true}
                  />
                </div>
                <div className="right-half-field col-6">
                  <Input
                    id={
                      edit
                        ? `edit-address-zipcode`
                        : `add-address-zipcode${index}`
                    }
                    type="text"
                    value={addData?.zipcode?.value}
                    maxLength={7}
                    autoComplete={true}
                    placeholder={`${i18n?.zipcode}*`}
                    showError={addData?.zipcode?.error}
                    errorMessage={addData?.zipcode?.message}
                    label={`${i18n?.zipcode}*`}
                    onChange={e => handleAddChange(index, "zipcode", e)}
                    onBlur={e =>
                      onBlurInput(index, "zipcode", e?.target?.value)
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit ? `edit-address-phone` : `add-address-phone${index}`
                    }
                    type="text"
                    value={addData?.phone?.value}
                    label={`${i18n?.phone}*`}
                    prefix={
                      <img
                        src={phoneIcon?.src}
                        alt="phone-icon"
                        aria-hidden="true"
                      />
                    }
                    placeholder={`${i18n?.phone}*`}
                    onChange={e => handleAddChange(index, "phone", e)}
                    showError={addData?.phone?.error}
                    errorMessage={addData?.phone?.message}
                    onBlur={e => onBlurInput(index, "phone", e?.target?.value)}
                    haspopup={false}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit ? `edit-address-label` : `add-address-label${index}`
                    }
                    type="text"
                    value={addData?.addresstypes?.value}
                    label={`${i18n?.setDefaultLabel}`}
                    showError={addData?.addresstypes?.error}
                    errorMessage={addData?.addresstypes?.message}
                    maxLength={25}
                    onChange={e => handleAddChange(index, "addresstypes", e)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Checkbox
                    value={i18n?.primaryAddress}
                    onChange={e => handleAddChange(index, "label", e)}
                    checked={addData?.label?.show}
                  />
                </div>
              </div>
            </div>
          ))
        : null}
      {!edit && (
        <div
          className="address-form__addaddress gbh-data-layer"
          data-gbh-data-layer={getAddAnotherAddressAnalytics()}
        >
          <span role="button" tabIndex="0" onClick={() => handleAddAddress()}>
            <img src={PlusIcon?.src} alt="" />
            {i18n?.addAnotherAddress}
          </span>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <Button
            type="black"
            label={save}
            size="large"
            flexible={true}
            onClick={() => {
              handleSubmit()
            }}
            role="button"
          />
        </div>
      </div>
    </div>
  )
}

export default AddressForm
